import { useState } from 'react';
import { ChevronsUpDown } from 'lucide-react';
import Table from '@design-system/table/table';
import TableHeader from '@design-system/table/table-header';
import TableBody from '@design-system/table/table-body';
import TableRow from '@design-system/table/table-row';
import TableHead from '@design-system/table/table-head';
import TableCell from '@design-system/table/table-cell';
import { Badge, type BadgeProps } from '@design-system/badge';
import { cn } from '@design-system/helpers';
import type {
  PresentationColumn,
  PresentationRow,
  TablePresentation,
} from '../../types/presentation.types';
import {
  isNavigableEntity,
  type OpenRecordFn,
} from '../../hooks/use-open-record';

const EMPTY = '—';
const TRUNCATE_AT = 48;

/** Map a free-form status string to a sensible badge variant. */
function statusVariant(raw: string): BadgeProps['variant'] {
  const v = raw.trim().toLowerCase();
  if (
    /(^|\b)(active|paid|completed|complete|done|won|approved|success|confirmed|enrolled|resolved)(\b|$)/.test(
      v
    )
  ) {
    return 'success';
  }
  if (
    /(^|\b)(failed|fail|cancelled|canceled|rejected|overdue|lost|error|inactive|declined|expired|unpaid)(\b|$)/.test(
      v
    )
  ) {
    return 'danger';
  }
  if (
    /(^|\b)(pending|draft|in progress|in-progress|processing|on hold|on-hold|partial|awaiting|review|new)(\b|$)/.test(
      v
    )
  ) {
    return 'warning';
  }
  return 'default';
}

/** Long text truncated with a copy-to-clipboard affordance for the full value. */
function CopyableText({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  if (text.length <= TRUNCATE_AT) return <span>{text}</span>;

  const copy = async (e: React.MouseEvent) => {
    // Never bubble to a clickable row.
    e.stopPropagation();
    try {
      await navigator.clipboard?.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard unavailable (permissions / insecure context) — no-op.
    }
  };

  return (
    <span className='inline-flex max-w-full items-center gap-1'>
      <span
        className='truncate'
        title={text}
      >
        {text.slice(0, TRUNCATE_AT)}…
      </span>
      <button
        type='button'
        onClick={copy}
        aria-label='Copy full value'
        className='shrink-0 text-xs text-gray-500 underline underline-offset-2 hover:text-gray-800'
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </span>
  );
}

function cellText(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  return typeof value === 'string' ? value : String(value);
}

function renderCell(col: PresentationColumn, row: PresentationRow) {
  const text = cellText(row[col.key]);
  if (text === null) {
    return <span className='text-gray-400'>{EMPTY}</span>;
  }
  if (col.type === 'badge') {
    return <Badge variant={statusVariant(text)}>{text}</Badge>;
  }
  return (
    <span
      className='block truncate text-sm font-medium leading-[18px] text-gray-800'
      title={text}
    >
      <CopyableText text={text} />
    </span>
  );
}

/**
 * Figma table surface (node 20420:441812) for the assistant table modal.
 * Columns/rows come from the BE presentation block.
 */
export function AgentDataTable({
  block,
  onOpenRecord,
  hideTitle = false,
  columnSortHint = false,
}: {
  block: TablePresentation;
  onOpenRecord?: OpenRecordFn;
  /** Title is shown in the modal chrome — hide the inline one. */
  hideTitle?: boolean;
  /** Decorative sort chevrons on header cells (Figma). */
  columnSortHint?: boolean;
}) {
  const { columns, rows, entity, title, omittedCount = 0 } = block;
  const navigable = isNavigableEntity(entity) && Boolean(onOpenRecord);
  const firstKey = columns[0]?.key;

  return (
    <div
      data-slot='agent-data-table'
      className='w-full max-w-full overflow-hidden bg-white'
    >
      {!hideTitle && title && (
        <div className='px-4 pt-3 text-sm font-medium text-gray-800'>
          {title}
        </div>
      )}
      <Table containerClassName='max-w-full bg-transparent'>
        <TableHeader>
          <TableRow className='border-0 hover:bg-transparent'>
            {columns.map((c) => (
              <TableHead
                key={c.key}
                className='h-14 min-w-[8rem] whitespace-nowrap border-b-0 border-r border-primary-200 bg-primary-50 px-3 text-sm font-semibold capitalize text-gray-800 last:border-r-0'
              >
                <span className='flex items-center justify-between gap-2'>
                  <span className='truncate'>{c.label}</span>
                  {columnSortHint && (
                    <ChevronsUpDown
                      className='size-3 shrink-0 text-gray-600'
                      aria-hidden
                    />
                  )}
                </span>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => {
            const id = row.id;
            const clickable =
              navigable && id !== undefined && id !== null && id !== '';
            const zebra = i % 2 === 1;
            return (
              <TableRow
                key={id ?? i}
                data-clickable={clickable ? 'true' : undefined}
                onClick={
                  clickable ? () => onOpenRecord?.(entity, id) : undefined
                }
                className={cn(
                  'h-14 border-0 hover:bg-transparent',
                  zebra ? 'bg-primary-30' : 'bg-white',
                  clickable && 'cursor-pointer'
                )}
              >
                {columns.map((c) => {
                  const isFirst = c.key === firstKey;
                  const primary = cellText(row[c.key]);
                  return (
                    <TableCell
                      key={c.key}
                      className='max-w-[10rem] border-b-0 bg-transparent px-3 py-0 align-middle text-sm text-gray-800'
                    >
                      {isFirst ? (
                        <div className='flex min-w-0 flex-col gap-0.5'>
                          <span
                            className='truncate text-sm font-medium leading-[18px] text-gray-800'
                            title={primary ?? undefined}
                          >
                            {primary ?? (
                              <span className='text-gray-400'>{EMPTY}</span>
                            )}
                          </span>
                          {id !== undefined && id !== null && id !== '' && (
                            <span className='truncate text-sm leading-[18px] text-gray-600'>
                              #{id}
                            </span>
                          )}
                        </div>
                      ) : (
                        renderCell(c, row)
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {omittedCount > 0 && (
        <div className='px-4 py-2 text-xs text-gray-600'>
          Showing {rows.length} of {rows.length + omittedCount}
        </div>
      )}
    </div>
  );
}
